import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { updateBarbershopBasicInfo, deleteBarbershopAndAllData } from '../services/barbershopService';
import toast from 'react-hot-toast';

/**
 * Modal para editar informações de uma barbearia (Admin)
 */
const EditBarbershopModal = ({ isOpen, onClose, barbershop, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      city: '',
      bannerUrl: '',
      isSponsored: false,
    },
  });

  // Atualizar valores quando barbershop mudar
  useEffect(() => {
    if (barbershop) {
      reset({
        name: barbershop.name || '',
        description: barbershop.description || '',
        city: barbershop.city || '',
        bannerUrl: barbershop.bannerUrl || '',
        isSponsored: barbershop.isSponsored || false,
      });
    }
  }, [barbershop, reset]);

  const onSubmit = async (data) => {
    if (!barbershop?.id) return;

    setIsSubmitting(true);
    
    const result = await updateBarbershopBasicInfo(barbershop.id, {
      name: data.name,
      description: data.description,
      city: data.city,
      bannerUrl: data.bannerUrl,
      isSponsored: data.isSponsored,
    });

    setIsSubmitting(false);

    if (result.success) {
      toast.success('Barbearia atualizada com sucesso!');
      onSuccess?.();
      onClose();
    } else {
      toast.error(result.error || 'Erro ao atualizar barbearia');
    }
  };

  const handleDeleteBarbershop = async () => {
    if (!barbershop?.id) return;
    if (deleteConfirmText !== barbershop.name) {
      toast.error('O nome digitado não confere. Digite o nome exato da barbearia para confirmar.');
      return;
    }

    setIsDeleting(true);

    const result = await deleteBarbershopAndAllData(barbershop.id);

    setIsDeleting(false);

    if (result.success) {
      const counts = result.deletedCounts || {};
      toast.success(
        `Barbearia "${barbershop.name}" deletada com sucesso! ` +
        `(${counts.appointments || 0} agendamentos, ${counts.availableSlots || 0} horários, ` +
        `${counts.reviews || 0} avaliações removidos)`
      );
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
      onSuccess?.();
      onClose();
    } else {
      toast.error(result.error || 'Erro ao deletar barbearia');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Editar Barbearia">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Nome */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome da Barbearia *
          </label>
          <input
            type="text"
            {...register('name', { required: 'Nome é obrigatório', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })}
            className="w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Barbearia Premium"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        {/* Descrição */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descrição *
          </label>
          <textarea
            {...register('description', { required: 'Descrição é obrigatória', minLength: { value: 10, message: 'Mínimo 10 caracteres' } })}
            className="w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            rows={3}
            placeholder="Descreva a barbearia..."
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
          )}
        </div>

        {/* Cidade */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cidade *
          </label>
          <input
            type="text"
            {...register('city', { required: 'Cidade é obrigatória' })}
            className="w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="São Paulo"
          />
          {errors.city && (
            <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
          )}
        </div>

        {/* Banner URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL do Banner
          </label>
          <input
            type="url"
            {...register('bannerUrl')}
            className="w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="https://..."
          />
          {errors.bannerUrl && (
            <p className="mt-1 text-sm text-red-600">{errors.bannerUrl.message}</p>
          )}
        </div>

        {/* Patrocinada */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isSponsored"
            {...register('isSponsored')}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="isSponsored" className="ml-2 block text-sm text-gray-900">
            Barbearia Patrocinada
          </label>
        </div>

        {/* Ações */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </div>
      </form>

      {/* Zona de Perigo - Deletar Barbearia */}
      <div className="mt-6 pt-4 border-t-2 border-red-200">
        <h3 className="text-lg font-semibold text-red-600 mb-2">⚠️ Zona de Perigo</h3>
        
        {!showDeleteConfirm ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                Deletar esta barbearia e todos os dados relacionados.
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Agendamentos, horários, avaliações, lista de espera e convites serão removidos permanentemente.
              </p>
            </div>
            <Button
              type="button"
              variant="danger"
              onClick={() => setShowDeleteConfirm(true)}
              className="ml-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium shrink-0"
            >
              Deletar Barbearia
            </Button>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-300 rounded-lg p-4 space-y-3">
            <p className="text-sm text-red-700 font-medium">
              Tem certeza? Esta ação é <strong>irreversível</strong>. Todos os dados serão perdidos permanentemente.
            </p>
            <p className="text-sm text-red-600">
              Digite <strong>"{barbershop?.name}"</strong> para confirmar:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full border border-red-300 rounded-lg shadow-sm p-2 focus:ring-red-500 focus:border-red-500 text-sm"
              placeholder="Digite o nome da barbearia..."
              disabled={isDeleting}
            />
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                disabled={isDeleting}
              >
                Cancelar
              </Button>
              <button
                type="button"
                onClick={handleDeleteBarbershop}
                disabled={isDeleting || deleteConfirmText !== barbershop?.name}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
                  isDeleting || deleteConfirmText !== barbershop?.name
                    ? 'bg-red-300 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {isDeleting ? 'Deletando...' : 'Confirmar Exclusão Permanente'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EditBarbershopModal;
